import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import { observer } from "mobx-react";
import { Card, Icon, Tag, Typography, Checkbox, ButtonMenu, CircularProgress } from "@ann/web-components";
import { CardWrapper, CardTop, CardHeader, CardHeaderTitle, CardMeta, CardMetaItem } from "./Card.styled";
import { ModalBase } from "../../../../components/Modal";

//  PERFORMANCE: observer for MobX reactivity, memo for performance
const DriveCard = observer(({ vaultUuid, name, selectMode, onSelect, selectedItems, setShowSlideoutDriveDetail, removeAccessLoading }) => {
    const [showSlideout, setShowSlideout] = useState(false);
    const [showRemoveAccessModal, setShowRemoveAccessModal] = useState(false);
    const [removingAccess, setRemovingAccess] = useState(false);
    
    //  PERFORMANCE: Memoized selection check - only recalculates when selectedItems changes
    const isSelected = useMemo(() => {
        return selectedItems.includes(vaultUuid);
    }, [selectedItems, vaultUuid]);

    //  PERFORMANCE: Memoized callbacks - stable references
    const handleRemoveAccessClick = useCallback(() => {
        setShowRemoveAccessModal(true);
    }, []);

    const handleRemoveAccess = useCallback(() => {
        setShowRemoveAccessModal(false);
    }, []);

    const handleCheckboxChange = useCallback(() => {
        onSelect(vaultUuid);
    }, [onSelect, vaultUuid]);

    const handleMenuChange = useCallback((value) => {
        if (value === 'remove-access') {
            handleRemoveAccessClick();
        } else if (value === 'view-details') {
            setShowSlideoutDriveDetail(true);
        }
    }, [handleRemoveAccessClick, setShowSlideoutDriveDetail]);

    //  PERFORMANCE: Fixed useEffect - only depends on specific values, not entire array
    useEffect(() => {
        setRemovingAccess(removeAccessLoading && isSelected);
    }, [removeAccessLoading, isSelected]);


    return (
        <>
            <Card disabled={removingAccess}>
                <CardWrapper>
                    <CardTop>
                        <CardHeader>
                            {selectMode && (
                                <Checkbox
                                    onChange={handleCheckboxChange}
                                    size="medium"
                                    checked={isSelected}
                                    disabled={true}
                                />
                            )}
                            <Icon name="folder" size="medium" />
                            <CardHeaderTitle title={name}>
                                <Typography category="subtitle" size="medium" weight="bold" color="title" maxCharacters={30}>
                                    {name}
                                </Typography>
                            </CardHeaderTitle>
                            {removingAccess ? (
                                <CircularProgress />
                            ) : (
                                <ButtonMenu
                                    customIcon="dotsMenu"
                                    disableEffects
                                    iconPosition="right"
                                    label=""
                                    menuPosition="bottom"
                                    onChange={handleMenuChange}
                                    size="medium"
                                    type="tertiary"
                                    width="260px"
                                >
                                    <ButtonMenu.Menu>
                                        <ButtonMenu.Item
                                            label="Remove access"
                                            value="remove-access"
                                        />
                                        <ButtonMenu.Item
                                            label="View details"
                                            value="view-details"
                                        />
                                    </ButtonMenu.Menu>
                                </ButtonMenu>
                            )}
                        </CardHeader>

                        <Typography
                            category="body"
                            size="medium"
                            weight="regular"
                            color="body"
                        >
                            Example description of the content that could go inside.
                        </Typography>
                    </CardTop>

                    <CardMeta>
                        <CardMetaItem>
                            <Tag icon="person" size="medium" color="informative" />
                            <Typography category="body" size="small" weight="regular" color="subtitle">
                                jane doe
                            </Typography>
                        </CardMetaItem>

                        <CardMetaItem>
                            <Tag icon="filePresent" size="medium" color="informative" />
                            <Typography category="body" size="small" weight="regular" color="subtitle">
                                10
                            </Typography>
                        </CardMetaItem>
                        <CardMetaItem>
                            <Tag icon="calendarToday" size="medium" color="informative" />
                            <Typography category="body" size="small" weight="regular" color="subtitle">
                                2025-08-10
                            </Typography>
                        </CardMetaItem>
                    </CardMeta>
                </CardWrapper>
            </Card>
            {showRemoveAccessModal && (
                <ModalBase
                    isOpen={showRemoveAccessModal}
                    onClose={() => setShowRemoveAccessModal(false)}
                    onClick={handleRemoveAccess}
                    title={`Are you sure you want to remove access?`}
                    description={"Your agent won’t be able to use it for responses until you reconnect it."}
                    buttonTitle="Remove access"
                    disabled={true}
                    modalWidth="571px" />
            )}
        </>
    )
});

DriveCard.displayName = 'DriveCard';

export default memo(DriveCard);